import { useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { formatDate } from '@frontend/date';
import { http } from '@frontend/fetch';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  ConfirmationModal,
  PrimaryButton,
  Table,
  Text,
  TextField,
  useForm,
  useModalControl,
  XIconSmall,
  phone,
  useAlert,
} from '@frontend/design-system';
import { BlockedNumberCreatePayload, BlockedNumberType } from '../components/call-block/types';
import { useCallBlockMutations } from '../components/call-block/use-call-block-mutations';
import { logError } from '../utils/loggers';

export const BlockNumberForm = ({ phoneTenantId }: { phoneTenantId?: string }) => {
  const { t } = useTranslation('phone', { keyPrefix: 'call-blocking' });
  const alert = useAlert();
  const { formProps, getFieldProps, isComplete, reset } = useForm({
    fields: {
      phone: { required: true, type: 'phone' },
    },
    onSubmit: () => {
      createFn({
        tenantId: phoneTenantId || '',
        phoneNumber: {
          countryCode: 1,
          nationalNumber: Number(getFieldProps('phone').value) || undefined,
        },
      });
    },
  });

  const { create } = useCallBlockMutations(phoneTenantId || '');

  const createFn = (payload: BlockedNumberCreatePayload) =>
    create.mutate(payload, {
      onSuccess: () => {
        alert.success(t('Phone number blocked successfully.'));
        reset();
      },
      onError: (error) => {
        http.isHttpError(error) && error.message?.includes('blocked number not unique for create operation')
          ? alert.error(t('This number has already been blocked.'))
          : alert.error(t('Failed to add number.'));
        logError('BlockNumberForm:createBlockedNumberMutation | Failed to block number', {
          data: { phoneTenantId, error },
        });
      },
    });

  return (
    <form {...formProps}>
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(3),
        }}
      >
        <span
          css={{
            display: 'inline-block',
            width: '300px',
          }}
        >
          <TextField {...getFieldProps('phone')} label={t('Phone Number')} />
          <Text css={{ marginTop: `${theme.spacing(0.5)}` }} size='small' color='light'>
            {t('Enter the phone number you want to block')}
          </Text>
        </span>
        <PrimaryButton destructive type='submit' disabled={!isComplete} size='tiny'>
          {t('Block Number')}
        </PrimaryButton>
      </div>
    </form>
  );
};
const handleDateSort = (a: string, b: string) => {
  if (a === b) return 0;
  if (dayjs(a).isBefore(b)) return 1;
  return -1;
};

export const NewBlockedNumbersTable = ({
  data,
  phoneTenantId,
}: {
  data: BlockedNumberType[];
  phoneTenantId: string;
}) => {
  const alert = useAlert();
  const { t } = useTranslation('phone', { keyPrefix: 'call-blocking' });
  const { modalProps, triggerProps } = useModalControl();
  const [deleteNumberId, setDeleteNumberId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const { remove } = useCallBlockMutations(phoneTenantId);

  const removeFn = (id: string) =>
    remove.mutate(
      { id: id },
      {
        onSuccess: () => {
          alert.success(t('Phone number unblocked successfully.'));
        },
        onError: (error) => {
          alert.error(t('Failed to unblock number.'));
          logError('NewBlockedNumbersTable:removeBlockedNumberMutation | Failed to unblock number', {
            data: { phoneTenantId, blockedNumberId: id, error },
          });
        },
      }
    );

  return (
    <>
      <Table
        emptyStateConfig={{
          header: t('No Blocked Numbers'),
          type: 'sync_your_phone',
        }}
        colConfig={[
          {
            Header: t('Number'),
            accessor: (row) => row.phoneNumber?.nationalNumber?.toString() || 'N/A',
            id: 'number',
          },
          {
            Header: t('Date Blocked'),
            id: 'createdAt',
            sortType: (a, b) => {
              const dateA = a.original.createdAt || '';
              const dateB = b.original.createdAt || '';
              return handleDateSort(dateA, dateB);
            },
            accessor: (row) => {
              const createdAt = row.createdAt;
              if (!createdAt) return 'Unknown';
              const formattedDate = createdAt.split('.')[0] + 'Z';
              return formatDate(formattedDate, 'MMM D, YYYY h:mm A');
            },
          },
        ]}
        customToolbarRender={() => {
          return <BlockNumberForm phoneTenantId={phoneTenantId} />;
        }}
        rowActions={{
          actions: [
            {
              label: t('Remove'),
              name: 'remove',
              Icon: XIconSmall,
              color: 'light',
              onClick: (blockedNumber) => {
                setPhoneNumber(blockedNumber.phoneNumber.nationalNumber?.toString() || '');
                setDeleteNumberId(blockedNumber.id || '');
                triggerProps.onClick();
              },
            },
          ],
        }}
        wrapperStyle={css`
          .table-toolbar {
            justify-content: space-between;
            align-items: start;
          }
        `}
        hasResponsiveColWidths
        data={data}
        hasGlobalSearch
        fullHeight
      />
      <ConfirmationModal
        {...modalProps}
        title={t('Unblock Number')}
        confirmLabel={t('Unblock')}
        onConfirm={() => removeFn(deleteNumberId)}
        onClose={modalProps.onClose}
      >
        <section
          css={css`
            margin-bottom: 16px;
            text-align: center;
          `}
        >
          <Trans t={t} number={phoneNumber}>
            Are you sure you want to unblock
            <Text as='div'>{phone(phoneNumber)}?</Text>
          </Trans>
        </section>
      </ConfirmationModal>
    </>
  );
};
